import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { useToast } from "@/components/feedback";
import { useAccessToken, useTenantData } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import type { VoidPaymentRequest } from "@/openapi/generated";
import { PaymentApi } from "@/openapi/generated";

export const usePaymentVoid = (toast: ReturnType<typeof useToast>) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantData();

  return useMutation({
    mutationFn: async (voidPaymentRequest: VoidPaymentRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(PaymentApi, accessToken);
      await service.apiPaymentsVoidPut({
        voidPaymentRequest,
        xTenantID: tenantId,
      });
      return voidPaymentRequest;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === QueryKeys.Payments,
      });
    },
    onError: async (error) => {
      const message = await getApiErrorMessage(error, "Failed to void payment");
      toast({
        title: "Void failed",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });
};
